/**
 * iOS standalone (Home Screen) quirk: when the on-screen keyboard opens for a focused input, WebKit scrolls the *window*
 * (not our inner scroller) to reveal it. After the keyboard closes — especially when the input is unmounted by a route
 * change (OTP → Today) so no blur/focusout fires — the window can stay scrolled: the fixed app shell sits shifted up and
 * the area below it shows as a black bar. The document must never scroll in this app, so whenever the keyboard is not
 * open we pin the window (and the visual viewport) back to 0.
 */
export function installIOSViewportFix(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const ua = navigator.userAgent;
  const isIOS = /iP(hone|ad|od)/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!isIOS) return () => undefined;
  const vv = window.visualViewport;
  const keyboardOpen = () => {
    const el = document.activeElement as HTMLElement | null;
    const editing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    const shrunk = !!vv && vv.height < window.innerHeight - 120;
    return editing && shrunk;
  };
  // Containers iOS may scroll to reveal a focused input even though they are not scrollable for the user.
  const clipped = () => [document.documentElement, document.body, document.getElementById('root'), ...Array.from(document.querySelectorAll<HTMLElement>('.m-app-shell'))].filter((e): e is HTMLElement => !!e);
  const shifted = () =>
    window.scrollY !== 0 || window.scrollX !== 0 || (!!vv && (vv.offsetTop > 0.5 || vv.offsetLeft > 0.5)) || clipped().some((e) => e.scrollTop !== 0 || e.scrollLeft !== 0);
  let raf = 0;
  const snap = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      if (keyboardOpen() || !shifted()) return;
      window.scrollTo(0, 0);
      for (const e of clipped()) {
        if (e.scrollTop !== 0) e.scrollTop = 0;
        if (e.scrollLeft !== 0) e.scrollLeft = 0;
      }
    });
  };
  const settle = () => {
    // keyboard dismissal animates; snap a few times over ~600ms
    snap();
    setTimeout(snap, 120);
    setTimeout(snap, 350);
    setTimeout(snap, 650);
  };
  // Standalone sizing quirk: iOS occasionally launches a Home Screen app with a layout viewport that is shorter than
  // the visual viewport (a phantom toolbar inset the size of Safari's bottom bar). A fixed inset:0 shell then stops
  // short and the root background shows below it. When the visual viewport is taller, size the shell to it.
  const standalone = () => (navigator as Navigator & { standalone?: boolean }).standalone === true || matchMedia('(display-mode: standalone)').matches;
  const heal = () => {
    const shell = document.querySelector<HTMLElement>('.m-app-shell');
    if (!shell || !vv || !standalone()) return;
    const extra = vv.height - window.innerHeight;
    if (extra > 8 && !keyboardOpen()) {
      shell.style.height = `${Math.round(vv.height)}px`;
      shell.style.bottom = 'auto';
    } else if (shell.style.height) {
      shell.style.height = '';
      shell.style.bottom = '';
    }
  };
  const onResize = () => {
    heal();
    settle();
  };
  vv?.addEventListener('resize', onResize);
  window.addEventListener('resize', heal);
  heal();
  vv?.addEventListener('scroll', snap);
  window.addEventListener('scroll', snap, { passive: true });
  document.addEventListener('focusout', settle, true);
  window.addEventListener('orientationchange', settle);
  window.addEventListener('pageshow', settle);
  document.addEventListener('visibilitychange', settle);
  const tick = window.setInterval(snap, 1500); // last resort: iOS sometimes shifts without firing anything
  settle();
  return () => {
    vv?.removeEventListener('resize', onResize);
    window.removeEventListener('resize', heal);
    vv?.removeEventListener('scroll', snap);
    window.removeEventListener('scroll', snap);
    document.removeEventListener('focusout', settle, true);
    window.removeEventListener('orientationchange', settle);
    window.removeEventListener('pageshow', settle);
    document.removeEventListener('visibilitychange', settle);
    window.clearInterval(tick);
    cancelAnimationFrame(raf);
  };
}
